import { computed, ref, watch, type Ref } from 'vue'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'
import { useHttp, useTranslate } from '@/shared/lib'
import { useNotification } from '@/shared/ui'
import type { InventoryMapObjectTypes } from '@/widgets/InventoryMap/model/types'
import { buildCoordinatesFromCenter } from '@/features/Inventory/CreatePassportV2Batch/model/buildCoordinatesFromCenter.ts'
import { resolveGeometryType } from '@/features/Inventory/CreatePassportV2Batch/model/resolveGeometryType.ts'
import {
  clearPlaceOnMapQuery,
  parsePlaceObjectIdFromQuery,
} from './usePlaceOnMapNavigation.ts'
import {
  buildPreviewTypeFromFullTypes,
  findInventTypeIdFromPassportRow,
} from './resolvePlacePreviewType.ts'

type PlaceClickEvent = {
  coordinates?: number[]
}

export function usePlaceInventObjectOnMap(options: {
  route: RouteLocationNormalizedLoaded
  router: Router
  mapGroupId: Ref<string | number>
  inventObjectTypes: Ref<InventoryMapObjectTypes[]>
  selectedObjects: Ref<number[]>
  relocate: (objects: { object_id: number; coordinates: number[][] | number[][][] }[]) => Promise<void>
  onPlaced: () => void
}) {
  const http = useHttp()
  const { trs } = useTranslate()
  const { addSuccess, addError } = useNotification()

  const placeObjectId = computed(() => parsePlaceObjectIdFromQuery(options.route.query))
  const placePreviewType = ref<InventoryMapObjectTypes | null>(null)
  const placeInventTypeId = ref<number | null>(null)
  const placePending = ref(false)
  const placeLoading = ref(false)

  const isPlaceMode = computed(() => placeObjectId.value != null)

  async function loadPlaceMeta(objectId: number) {
    placeLoading.value = true
    placePreviewType.value = null
    placeInventTypeId.value = null
    try {
      const groupId = encodeURIComponent(String(options.mapGroupId.value))
      const [passportRes, typesRes] = await Promise.all([
        http.get(`/inventory/objects/passport?object_ids=${objectId}`),
        http.get(`/inventory/objects/${groupId}/object-types/full`),
      ])
      const list = Array.isArray(passportRes.data) ? passportRes.data : []
      const row = list[0] as Record<string, unknown> | undefined
      const typeId = findInventTypeIdFromPassportRow(row)
      if (typeId == null) return

      placeInventTypeId.value = typeId

      const fromFull = buildPreviewTypeFromFullTypes(typesRes.data, typeId)
      if (fromFull) {
        placePreviewType.value = fromFull
        return
      }

      const found = options.inventObjectTypes.value.find((t) => t.id === typeId)
      if (found) placePreviewType.value = found
    } catch {
      addError(trs('request.unknown_error'))
    } finally {
      placeLoading.value = false
    }
  }

  watch(
    placeObjectId,
    (id) => {
      if (!id) {
        placePreviewType.value = null
        placeInventTypeId.value = null
        return
      }
      options.selectedObjects.value = [id]
      void loadPlaceMeta(id)
    },
    { immediate: true },
  )

  watch(
    () => options.inventObjectTypes.value,
    () => {
      const id = placeInventTypeId.value
      if (!id || placePreviewType.value) return
      const found = options.inventObjectTypes.value.find((t) => t.id === id)
      if (found) placePreviewType.value = found
    },
  )

  function exitPlaceMode() {
    void clearPlaceOnMapQuery(options.router, options.route)
  }

  async function handlePlaceMapClick(event: PlaceClickEvent) {
    const objectId = placeObjectId.value
    const clickCoords = event.coordinates
    if (!objectId || placePending.value || !Array.isArray(clickCoords) || clickCoords.length < 2) {
      return
    }

    const [lng, lat] = clickCoords
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return

    /** Тип фигуры — из placePreviewType (object-types/full); inventObjectTypes карты не заполняется */
    if (!placePreviewType.value) {
      await loadPlaceMeta(objectId)
    }
    if (!placePreviewType.value) {
      addError(trs('request.unknown_error'))
      return
    }

    const geometryType = resolveGeometryType(
      placeInventTypeId.value,
      [placePreviewType.value, ...options.inventObjectTypes.value],
    )
    const built = buildCoordinatesFromCenter({ longitude: lng, latitude: lat }, geometryType)
    if (!built.length) return

    /** relocate ждёт строгий формат: для Polygon — вложенное кольцо [[[...]]] */
    const coordinates = geometryType === 'Polygon' ? [built] : built

    placePending.value = true
    try {
      await options.relocate([{ object_id: objectId, coordinates }])
      addSuccess(trs('itf.map_place_success'))
      exitPlaceMode()
      options.onPlaced()
    } catch {
      addError(trs('request.unknown_error'))
    } finally {
      placePending.value = false
    }
  }

  return {
    placeObjectId,
    placePreviewType,
    isPlaceMode,
    placePending,
    placeLoading,
    handlePlaceMapClick,
    exitPlaceMode,
  }
}
